/* eslint-disable no-case-declarations */
import React, { createContext, useReducer, useContext, useEffect } from 'react';
import LocaleContext from 'locales';
import uploadManager from '../utils/managers/uploadManager';

// 초기 상태
const initialState = {
  items: [],
  progress: {},
  status: {},
  speed: {},
  timeRemaining: {},
  visible: false,
  collapsed: false,
};

// 액션 타입
const ActionTypes = {
  ADD_ITEMS: 'ADD_ITEMS',
  UPDATE_PROGRESS: 'UPDATE_PROGRESS',
  UPDATE_STATUS: 'UPDATE_STATUS',
  UPDATE_SPEED: 'UPDATE_SPEED',
  UPDATE_TIME_REMAINING: 'UPDATE_TIME_REMAINING',
  REMOVE_ITEMS: 'REMOVE_ITEMS',
  TOGGLE_VISIBILITY: 'TOGGLE_VISIBILITY',
  TOGGLE_COLLAPSED: 'TOGGLE_COLLAPSED',
  CLEAR_COMPLETED: 'CLEAR_COMPLETED',
};

// 리듀서
function uploadReducer(state, action) {
  switch (action.type) {
    case ActionTypes.ADD_ITEMS:
      return {
        ...state,
        items: [...state.items, ...action.payload],
        visible: true,
      };
    case ActionTypes.UPDATE_PROGRESS:
      return {
        ...state,
        progress: {
          ...state.progress,
          [action.payload.id]: action.payload.progress,
        },
      };
    case ActionTypes.UPDATE_STATUS:
      return {
        ...state,
        status: {
          ...state.status,
          [action.payload.id]: action.payload.status,
        },
      };
    case ActionTypes.UPDATE_SPEED:
      return {
        ...state,
        speed: {
          ...state.speed,
          [action.payload.id]: action.payload.speed,
        },
      };
    case ActionTypes.UPDATE_TIME_REMAINING:
      return {
        ...state,
        timeRemaining: {
          ...state.timeRemaining,
          [action.payload.id]: action.payload.timeRemaining,
        },
      };
    case ActionTypes.REMOVE_ITEMS:
      return {
        ...state,
        items: state.items.filter((item) => !action.payload.includes(item.uid)),
      };
    case ActionTypes.TOGGLE_VISIBILITY:
      return {
        ...state,
        visible: action.payload !== undefined ? action.payload : !state.visible,
      };
    case ActionTypes.TOGGLE_COLLAPSED:
      return {
        ...state,
        collapsed: action.payload !== undefined ? action.payload : !state.collapsed,
      };
    case ActionTypes.CLEAR_COMPLETED:
      const completedIds = state.items
        .filter((item) => ['completed', 'error', 'cancelled'].includes(state.status[item.uid]))
        .map((item) => item.uid);

      const newProgress = { ...state.progress };
      const newStatus = { ...state.status };
      const newSpeed = { ...state.speed };
      const newTimeRemaining = { ...state.timeRemaining };

      completedIds.forEach((id) => {
        delete newProgress[id];
        delete newStatus[id];
        delete newSpeed[id];
        delete newTimeRemaining[id];
      });

      return {
        ...state,
        items: state.items.filter((item) => !completedIds.includes(item.uid)),
        progress: newProgress,
        status: newStatus,
        speed: newSpeed,
        timeRemaining: newTimeRemaining,
        visible: state.items.length > completedIds.length, // 남은 항목이 없으면 패널 숨기기
      };
    default:
      return state;
  }
}

// Context 생성
const UploadContext = createContext();

// Provider 컴포넌트
export function UploadProvider({ children }) {
  const [state, dispatch] = useReducer(uploadReducer, initialState);
  const { locale } = useContext(LocaleContext);

  // 컴포넌트가 마운트될 때 uploadManager에 상태 업데이트 함수 등록
  useEffect(() => {
    const updater = {
      addItems: (items) => {
        dispatch({ type: ActionTypes.ADD_ITEMS, payload: items });
      },
      updateProgress: (id, progress) => {
        dispatch({
          type: ActionTypes.UPDATE_PROGRESS,
          payload: { id, progress },
        });
      },
      updateStatus: (id, status) => {
        dispatch({
          type: ActionTypes.UPDATE_STATUS,
          payload: { id, status },
        });
      },
      updateSpeed: (id, speed) => {
        dispatch({
          type: ActionTypes.UPDATE_SPEED,
          payload: { id, speed },
        });
      },
      updateTimeRemaining: (id, timeRemaining) => {
        dispatch({
          type: ActionTypes.UPDATE_TIME_REMAINING,
          payload: { id, timeRemaining },
        });
      },
      removeItems: (ids) => {
        dispatch({ type: ActionTypes.REMOVE_ITEMS, payload: ids });
      },
      clearCompleted: () => {
        dispatch({ type: ActionTypes.CLEAR_COMPLETED });
      },
      getItems: () => state.items,
      getStatus: (id) => state.status[id],
    };
    uploadManager.setStateUpdater(updater);

    // 업로드 매니저에 locale 주입
    if (locale && locale.resource) {
      uploadManager.setLocaleResource(locale.resource);
    }

    return () => {
      uploadManager.setStateUpdater(null);
    };
  }, [locale]);

  // Context 값
  const contextValue = {
    ...state,
    toggleVisibility: (visible) => {
      dispatch({ type: ActionTypes.TOGGLE_VISIBILITY, payload: visible });
    },
    toggleCollapsed: (collapsed) => {
      dispatch({ type: ActionTypes.TOGGLE_COLLAPSED, payload: collapsed });
    },
    clearCompleted: () => {
      dispatch({ type: ActionTypes.CLEAR_COMPLETED });
    },
    addFiles: uploadManager.addFiles,
    cancelUpload: uploadManager.cancelUpload,
    cancelAllUploads: uploadManager.cancelAllUploads,
  };

  return <UploadContext.Provider value={contextValue}>{children}</UploadContext.Provider>;
}

// Context 사용을 위한 커스텀 훅
export function useUploadContext() {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUploadContext must be used within an UploadProvider');
  }
  return context;
}
